import type { Lesson } from './types'

/**
 * Vowel marks (harakat) and related signs - the second stage of a Qaida
 * course, right after the bare alphabet: how letters actually sound once a
 * vowel mark is added.
 */
export const harakatLessons: Lesson[] = [
  {
    id: 'harakat-short-vowels',
    title: 'Short Vowels: Fatha, Kasra, Damma',
    summary: 'The three short vowel marks that turn a silent letter into a sound.',
    explanation: [
      'A bare Arabic letter has no sound by itself - it needs a vowel mark (harakah) above or below it. There are three short vowels:',
      'Fatha ( َ ) - a small diagonal stroke above the letter, giving a short "a" sound.',
      'Kasra ( ِ ) - the same stroke below the letter, giving a short "i" sound.',
      'Damma ( ُ ) - a small waw-shaped mark above the letter, giving a short "u" sound.',
      'Each is held for exactly one count (harakah) - not stretched, not clipped.',
    ],
    colorLegend: [{ color: 'neutral', label: 'Vowel mark' }],
    examples: [
      { arabic: 'بَ', transliteration: 'ba' },
      { arabic: 'بِ', transliteration: 'bi' },
      { arabic: 'بُ', transliteration: 'bu' },
      { arabic: 'تَ', transliteration: 'ta' },
      { arabic: 'تِ', transliteration: 'ti' },
      { arabic: 'تُ', transliteration: 'tu' },
      { arabic: 'كَتَبَ', transliteration: 'kataba', meaning: 'he wrote' },
    ],
  },
  {
    id: 'harakat-sukun',
    title: 'Sukun (No Vowel)',
    summary: 'The small circle that means "this letter has no vowel - stop the sound here."',
    explanation: [
      'Sukun ( ْ ) is a small circle placed above a letter to show it carries no vowel at all. The letter is pronounced quickly and cleanly, with no vowel sound trailing after it - it simply closes the syllable before it.',
      'Reading a word smoothly means blending a vowelled letter into a following sukun letter without a pause between them.',
    ],
    examples: [
      { arabic: 'بْ', transliteration: 'b' },
      { arabic: 'مِنْ', transliteration: 'min', meaning: 'from' },
      { arabic: 'قُلْ', transliteration: 'qul', meaning: 'say' },
    ],
  },
  {
    id: 'harakat-tanween',
    title: 'Tanween (Double Vowels)',
    summary: 'Doubling a vowel mark adds an "n" sound at the end of a word.',
    explanation: [
      'Tanween is a doubled vowel mark, usually found only at the end of a word, and it adds an "n" sound after the vowel:',
      'Fathatain ( ً ) - sounds like "an" (usually written with an extra alif: ـًا).',
      'Kasratain ( ٍ ) - sounds like "in".',
      'Dammatain ( ٌ ) - sounds like "un".',
      'Tanween is one of the two things (along with noon sakinah) that the whole next module of rules - izhar, idgham, iqlab, and ikhfa - applies to.',
    ],
    examples: [
      { arabic: 'كِتَابًا', transliteration: 'kitāban', meaning: 'a book' },
      { arabic: 'كِتَابٍ', transliteration: 'kitābin', meaning: 'of a book' },
      { arabic: 'كِتَابٌ', transliteration: 'kitābun', meaning: 'a book (subject)' },
    ],
  },
  {
    id: 'harakat-shaddah',
    title: 'Shaddah (Doubled Letter)',
    summary: 'A small ⟨w⟩-shaped mark meaning "pronounce this letter twice, pressed together."',
    explanation: [
      'Shaddah ( ّ ) sits above a letter to show it is doubled: the letter is pressed and held firmly, as if it were written twice in a row - once with sukun, once with its vowel.',
      'A letter with shaddah is always given extra emphasis and held slightly longer than a normal letter.',
    ],
    colorLegend: [{ color: 'ghunnah', label: 'Nasalized when the doubled letter is م or ن' }],
    examples: [
      {
        arabic: 'رَبَّنَا',
        transliteration: 'rabbanā',
        meaning: 'our Lord',
        highlights: [{ text: 'ب', color: 'neutral' }],
      },
      {
        arabic: 'إِنَّ',
        transliteration: 'inna',
        meaning: 'indeed',
        highlights: [{ text: 'ن', color: 'ghunnah' }],
      },
      {
        arabic: 'الْحَقُّ',
        transliteration: 'al-ḥaqqu',
        meaning: 'the truth',
      },
    ],
  },
  {
    id: 'harakat-madd-letters',
    title: 'Madd Letters (Natural Elongation)',
    summary: 'Alif, Waw, and Yaa stretch a vowel to two counts instead of one.',
    explanation: [
      'Three letters double as elongation (madd) letters when they appear as the natural extension of a matching short vowel: Alif (ا) after a fatha, Waw (و) after a damma, and Yaa (ي) after a kasra.',
      'When this happens the vowel is stretched to two counts instead of one - this is called Madd Tabee\'i (natural madd), the most basic of all the madd rules covered later in this course.',
    ],
    colorLegend: [{ color: 'madd', label: 'Elongated vowel (2 counts)' }],
    examples: [
      { arabic: 'قَالَ', transliteration: 'qāla', meaning: 'he said', highlights: [{ text: 'ا', color: 'madd' }] },
      { arabic: 'يَقُولُ', transliteration: 'yaqūlu', meaning: 'he says', highlights: [{ text: 'و', color: 'madd' }] },
      { arabic: 'قِيلَ', transliteration: 'qīla', meaning: 'it was said', highlights: [{ text: 'ي', color: 'madd' }] },
    ],
  },
]
